/* eslint-disable react-hooks/exhaustive-deps */
import { ExclamationCircleFilled } from "@ant-design/icons";
import { Avatar, Checkbox, Divider, Select, Tag } from "antd";
import moment from "moment";
import React, { useEffect, useState } from "react";
import styles from "styles/components/questionAnswersModal.module.scss";
import { QUESTION_TYPES, SORTING_OPTIONS } from "utils/constants";
import { smoothScroll } from "utils/utils";
import AnswerContent from "./answerContent";
import Clarifications from "./clarifications";

const AnswerListItem = ({
  answer,
  question,
  users,
  isAnonymous,
  showClarifications,
  isModalOpen,
  refreshAnswers,
  isLast,
}) => {
  const user = answer.userId ? users?.[answer.userId] : null;

  return (
    <div
      className={styles.answer}
      key={`answer_${answer.responseId}_${answer.content || ""}${answer.comment || ""}${answer.userId || ""}`}
      id={`answer_${answer.responseId}`}
    >
      <div className={styles.answerInner}>
        <div className={styles.answerContent}>
          <div className={styles.userWrapper}>
            {user ? (
              <div className={styles.userElement}>
                <div className={styles.avatarWrapper}>
                  <Avatar
                    size={48}
                    src={user.profilePicture}
                    className={styles.avatar}
                  />
                  <div className={styles.avatarStatus}>
                    {user.incomplete ? (
                      <ExclamationCircleFilled
                        style={{ color: "orange" }}
                        className={styles.statusIcon}
                      />
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
                <div className={styles.usernameWrapper}>
                  <div className={styles.username}>{user.name}</div>
                  <div className={styles.date}>
                    {user.incomplete
                      ? `Last answered at ${moment(user.lastAnsweredAt).format("L - HH:mm")}`
                      : `Submited on ${moment(user.submittedAt).format("L - HH:mm")}`}
                  </div>
                </div>
              </div>
            ) : (
              <div
                className={styles.usernameWrapper}
                style={{ marginLeft: 0, marginBottom: 8 }}
              >
                <div className={styles.username}>
                  User anonymous
                  {answer.incomplete ? (
                    <ExclamationCircleFilled
                      style={{ color: "orange", marginLeft: 4 }}
                      className={styles.statusIcon}
                    />
                  ) : (
                    <></>
                  )}
                </div>
                <div className={styles.date}>
                  {answer.incomplete
                    ? `Last answered at ${moment(answer.date).format("L - HH:mm")}`
                    : `Submited on ${moment(answer.date).format("L - HH:mm")}`}
                </div>
              </div>
            )}
          </div>
          <AnswerContent answer={answer} question={question} />
        </div>
        {showClarifications && isModalOpen ? (
          <Clarifications
            answer={answer}
            refreshAnswers={refreshAnswers}
            question={question}
            incomplete={answer.incomplete}
            users={users}
            isAnonymous={isAnonymous}
          />
        ) : null}
      </div>
      {!isLast && <Divider style={{ margin: 0, marginBottom: 24 }} />}
    </div>
  );
};

/**
 * Reusable list of answers for a question. Used in QuestionAnswersModal and
 * inline in survey-details (e.g. when bot === 'true').
 *
 * onlyWithComment: pass to control filter (controlled). Omit to use internal state.
 * onlyWithCommentDefaultValue: initial value when onlyWithComment is not passed.
 * onOnlyWithCommentChange: optional callback when the "With comment" checkbox changes (for controlled mode).
 */
const QuestionAnswersList = ({
  question,
  users,
  isAnonymous = false,
  onlyWithComment: onlyWithCommentProp,
  onlyWithCommentDefaultValue = false,
  onOnlyWithCommentChange,
  defaultOpen,
  refreshAnswers = () => {},
  showFilters = true,
  showClarifications = true,
  isModalOpen = false,
}) => {
  const answers = question?.results?.answers?.map((a, i) => ({ ...a, i })) ?? [];
  const numberComments = answers.reduce(
    (acc, el) => (el.comment && el.comment.length > 0 ? acc + 1 : acc),
    0
  );

  const [onlyWithCommentState, setOnlyWithCommentState] = useState(
    onlyWithCommentDefaultValue
  );
  const isOnlyWithCommentControlled = onlyWithCommentProp !== undefined;
  const onlyWithComment = isOnlyWithCommentControlled
    ? onlyWithCommentProp
    : onlyWithCommentState;

  const setOnlyWithComment = (value) => {
    if (!isOnlyWithCommentControlled) setOnlyWithCommentState(value);
    onOnlyWithCommentChange?.(value);
  };

  const [sorting, setSorting] = useState(SORTING_OPTIONS.MOST_RECENT.value);
  const [scrollDone, setScrollDone] = useState(false);

  useEffect(() => {
    if (!isOnlyWithCommentControlled) {
      setOnlyWithCommentState(onlyWithCommentDefaultValue);
    }
  }, [onlyWithCommentDefaultValue, isOnlyWithCommentControlled]);

  useEffect(() => {
    if (defaultOpen && defaultOpen.length > 0) {
      setTimeout(() => {
        if (!scrollDone) {
          smoothScroll(`#answer_${defaultOpen}_thread`);
          setScrollDone(true);
        }
      }, 500);
    }
  }, [defaultOpen]);

  const filteredAnswers = answers
    ?.filter((a) => (onlyWithComment ? a.comment && a.comment.length > 0 : true))
    ?.filter((a) => a.content || a.comment)
    ?.sort(SORTING_OPTIONS[sorting].sortFunction) ?? [];

  const nbIncomplete = (
    isAnonymous
      ? filteredAnswers
      : Object.values(users || {})
  )?.reduce((acc, el) => (el.incomplete ? acc + 1 : acc), 0);

  return (
    <>
      {showFilters && question?.type !== QUESTION_TYPES.OPEN_ENDED && (
        <div className={styles.actions}>
          <Checkbox
            disabled={numberComments <= 0}
            checked={onlyWithComment}
            onChange={(e) => setOnlyWithComment(e.target.checked)}
          >
            With comment ({numberComments})
          </Checkbox>
          <div className={styles.actionsRight}>
            <Select
              disabled={answers?.length <= 0}
              style={{ width: 170 }}
              onChange={setSorting}
              options={Object.values(SORTING_OPTIONS)}
              value={sorting}
            />
          </div>
        </div>
      )}
      {showFilters && nbIncomplete > 0 ? (
        <Tag
          bordered={false}
          color={"grey"}
          style={{
            backgroundColor: "#ffa5002e",
            color: "#bb7900",
            fontSize: "16px",
            textAlign: "center",
            marginBottom: 22,
            cursor: "pointer",
            width: "100%",
            padding: "4px 8px",
          }}
        >
          <ExclamationCircleFilled style={{ color: "orange" }} />
          &nbsp;&nbsp;
          {`${nbIncomplete} incomplete ${
            nbIncomplete > 1 ? "responses are" : "response is"
          } listed`}
        </Tag>
      ) : null}
      {filteredAnswers.map((a, i) => (
        <AnswerListItem
          key={`${i}_answer_${a.responseId}_${a.content || ""}${a.comment || ""}${a.userId || ""}`}
          answer={a}
          question={question}
          users={users}
          isAnonymous={isAnonymous}
          showClarifications={showClarifications}
          isModalOpen={isModalOpen}
          refreshAnswers={refreshAnswers}
          isLast={i === filteredAnswers.length - 1}
        />
      ))}
    </>
  );
};

export default QuestionAnswersList;
